import { useEffect, useRef } from 'react';
import type { NextRouter } from 'next/router';
import type { MessageAttachment } from '@wix/ai-scheduling-assistant-common-lib/entities';
import type { ChatState, PendingAttachment } from '../../../../lib/use-chat';
import type { ExternalChannel } from '../../../../lib/external-channel';
import type { SendReferences } from '../../../../lib/send-references';
import { isSameOutreachHandoff } from '../../../../lib/outreach-retry-scope';
import { appendContactTokens, contactReferences, type PickedContact } from '../../../../lib/contact-references';
import { queryPath } from '../../../../lib/query-path';
import { PACK_HANDOFF_PARAM } from '../../../../lib/pack-handoff';

const HANDOFF_PARAMS = ['new', 'message', 'prefill', 'autosend', 'outreach', PACK_HANDOFF_PARAM] as const;

// Autosend the URL-provided prefill once and only once (chat handoffs: Pulse
// Edit, Task chat-about, Knowledge Edit). New-thread handoffs (`?new=1`) let
// send() lazy-create; existing-thread handoffs wait until useChat has the right
// conversation loaded. A home handoff can carry files too — wait until they're
// uploaded and send them WITH the text as one message.
export function useAutosend({
  shouldAutosend,
  prefill,
  handoffOutreachId,
  handoffTaskId,
  handoffContext,
  handoffAttachments,
  isNewConv,
  selectedId,
  prefillFromStore,
  consumePrefill,
  chat,
  router,
  pendingUploadFiles,
  uploadsInFlight,
  readyAttachments,
  pendingAttachments,
  setPendingAttachments,
  pendingContacts = null,
  contactsForSend = [],
  clearContactsForSend,
  lockedChannel,
  beginSendScroll,
  waitingForRecipient,
  turnInFlight = false,
}: {
  shouldAutosend: boolean;
  prefill: string | null;
  /**
   * The inbound outreach this prefill is REPLYING to, when the handoff came from the outreach thread
   * view. Sent as `references.outreachId` — the signal that tells the agent to relay the message with
   * the outreach tools rather than treat it as a request of its own. `references` is a Struct on the
   * wire, so this rides with no proto change (same as `mentionedAgentId`).
   */
  handoffOutreachId?: string | null;
  /**
   * The task card this prefill was opened from, sent as `references.taskId`. It is what lets the run
   * refuse to open a second task for the card the user is already looking at.
   */
  handoffTaskId?: string | null;
  /**
   * The handoff's HIDDEN context line, sent as the stream endpoint's top-level `context` — the same field
   * the phones fill (`AppRouter.createGoalContext` / `ChatContext.createGoalContext`). It is NOT a
   * reference: `references` is persisted onto the message row's metadata, while `context` is prompt
   * context for this one turn, which is what a "the Space you are creating in is <id>" line wants to be.
   */
  handoffContext?: string | null;
  /**
   * Attachments the handoff brings with it, already uploaded — today only a voice note recorded on
   * the home launcher. This is the ONE handoff whose `prefill` is legitimately empty: empty text +
   * an audio attachment IS the voice-note wire shape the harness transcribes, so the "no prefill,
   * nothing to send" guard below has to let it through.
   */
  handoffAttachments?: MessageAttachment[] | null;
  isNewConv: boolean;
  selectedId: string | null;
  // Where the pending prefill came from: the ActiveConversation store
  // (openChatWithMessage) rather than the URL query params. Governs how it's
  // cleared (consume the store intent vs. strip the params) and how strictly it
  // dedups (a store prefill is never re-fired by a reload).
  prefillFromStore: boolean;
  consumePrefill: () => void;
  chat: ChatState;
  router: NextRouter;
  pendingUploadFiles: File[] | null;
  uploadsInFlight: boolean;
  readyAttachments: PendingAttachment[];
  pendingAttachments: PendingAttachment[];
  setPendingAttachments: React.Dispatch<React.SetStateAction<PendingAttachment[]>>;
  /**
   * Contacts the home launcher handed off across the navigation — still waiting for
   * `useContactReferences`'s transfer effect to fold them into `contactsForSend`. Mirrors the
   * `pendingUploadFiles` gate: hold the send until the handoff has actually landed in state.
   * Omitted (default `null`) by a caller with no contact-attach flow of its own.
   */
  pendingContacts?: PickedContact[] | null;
  /** The picked-contacts state, already resolved and ready to ride this send as `references.contacts`.
   *  Defaults to `[]` for a caller with no contact-attach flow of its own. */
  contactsForSend?: PickedContact[];
  /** Clears `contactsForSend` once they've been folded into a send — mirrors `setPendingAttachments([])`. */
  clearContactsForSend?: () => void;
  // The open conversation is WhatsApp/SMS-owned (read-only on web) — never
  // autosend into it; the server guard would 403 it anyway.
  lockedChannel: ExternalChannel | null;
  // Glide the just-sent bubble up to the top so it's actually in view (and the
  // reply streams beneath it) — same as the manual submit path. Without this the
  // optimistic user message is appended but never scrolled into view, so it only
  // appears once the reply's auto-stick scroll fires.
  beginSendScroll: () => void;
  // The caller's recipient hasn't resolved yet — the chat is held behind the loader and there's no
  // identity to send under, so a handoff prefill must WAIT rather than fire a send into the void. The
  // effect re-runs once this flips false, sending the prefill the moment the recipient is ready.
  waitingForRecipient: boolean;
  /**
   * A turn is running (or about to): `send` refuses while one streams and this hook's one-shot latch
   * would still consume the handoff, silently dropping the prompt. Hold instead; the effect re-runs
   * when it clears. The accept landing relies on this: its prompt must land after the pack
   * announcement, not collide with it.
   */
  turnInFlight?: boolean;
}) {
  // One-shot per handoff; re-armed when the consumed handoff's params leave the
  // URL (the overlay never remounts, so a second CTA must re-arm this).
  const autosentRef = useRef(false);
  useEffect(() => {
    if (!shouldAutosend) autosentRef.current = false;
  }, [shouldAutosend]);

  useEffect(() => {
    if (autosentRef.current || !shouldAutosend) return;
    // Hold until the recipient resolves — the chat is behind the loader and there's no identity to
    // send under; the effect re-fires when this flips false.
    if (waitingForRecipient) return;
    // Hold while a turn is in flight — a send now would be refused and the handoff lost.
    if (turnInFlight) return;
    // Never autosend into a WhatsApp/SMS-owned (read-only) thread — the server
    // guard would 403 it anyway; the locked bar is already showing.
    if (lockedChannel) return;
    const readyForExisting = !isNewConv && chat.conversationId === selectedId && !chat.loading;
    const readyForNew = isNewConv && selectedId === null && chat.conversationId === null;
    if (!readyForExisting && !readyForNew) return;
    if ((pendingUploadFiles && pendingUploadFiles.length > 0) || uploadsInFlight) return;
    // Hold until the home launcher's picked-contacts handoff has landed in `contactsForSend` — the
    // transfer effect clears this on the same tick it folds them in, so the send below always sees
    // the final list rather than racing it.
    if (pendingContacts && pendingContacts.length > 0) return;

    // A voice note recorded on the home launcher: the audio IS the message and the text is
    // deliberately empty, which is the shape the harness transcribes. See `PendingPrefill.attachments`.
    const handoffAudio = handoffAttachments && handoffAttachments.length > 0 ? handoffAttachments : null;
    const contactRefs = contactsForSend.length > 0 ? contactReferences(contactsForSend) : undefined;
    // `prefill` alone is empty/null for a Home "+ → Contact" handoff with no typed draft — the
    // contact token is the only text — and for a home-recorded voice note, where there is no text at
    // all. Only bail once EVERY one of them is empty, or those handoffs can never send.
    const outgoingPrefill = contactRefs ? appendContactTokens(prefill ?? '', contactsForSend) : prefill ?? '';
    if (!outgoingPrefill && !handoffAudio) return;

    const clearHandoff = () => {
      if (prefillFromStore) {
        consumePrefill();
        return;
      }
      if (!selectedId) return;
      // Strip on the CURRENT route: the chat overlays whatever screen fired the handoff (a pack's Edit
      // continues the open thread from /packs/[slug]). Same strip as ChatSurface's onConversationCreated.
      const query: Record<string, string | string[] | undefined> = { ...router.query, conversation: selectedId };
      for (const key of HANDOFF_PARAMS) delete query[key];
      // A dynamic segment (`slug` on /packs/[slug]) rides router.query but is already in the path.
      const shown = { ...query };
      for (const [, param] of router.pathname.matchAll(/\[+\.{0,3}([^\]]+)\]+/g)) delete shown[param];
      void router.replace({ pathname: router.pathname, query }, queryPath(window.location.pathname, shown), { shallow: true });
    };

    if (readyForExisting && !prefillFromStore && prefill) {
      // Native thread composers send the selected ID directly and do not have this URL-history
      // shortcut. A previous untagged answer must not swallow the user's new scoped handoff.
      // Compared against `outgoingPrefill` (not the raw `prefill`) so a contact token appended by
      // the home launcher's "+ Contact" picker is part of what counts as "already sent".
      const alreadySent = chat.messages.some((m) => isSameOutreachHandoff(m, outgoingPrefill, handoffOutreachId));
      if (alreadySent) {
        autosentRef.current = true;
        clearHandoff();
        return;
      }
    }

    autosentRef.current = true;
    const sendable: MessageAttachment[] = [
      // The handoff's own attachments lead — a voice note IS the message, and anything the composer
      // happened to be holding follows it.
      ...(handoffAudio ?? []),
      ...readyAttachments.map((p) => ({
        fileName: p.attachment!.fileName,
        mimeType: p.attachment!.mimeType,
        url: p.attachment!.url,
        ...(p.attachment!.size !== undefined ? { size: p.attachment!.size } : {}),
      })),
    ];
    // The outreach/task/contact riders. Typed as `SendReferences` rather than built inline so the
    // extra keys are documented riders, not an object the compiler has to be talked out of rejecting.
    const references: SendReferences | undefined =
      handoffOutreachId || handoffTaskId || contactRefs
        ? {
            ...(handoffOutreachId ? { outreachId: handoffOutreachId } : {}),
            ...(handoffTaskId ? { taskId: handoffTaskId } : {}),
            ...contactRefs,
          }
        : undefined;
    chat.send(outgoingPrefill, sendable.length > 0 ? sendable : undefined, references, undefined, undefined, handoffContext ?? undefined);
    beginSendScroll();
    if (readyAttachments.length > 0) {
      pendingAttachments.forEach((p) => {
        if (p.previewUrl) URL.revokeObjectURL(p.previewUrl);
      });
      setPendingAttachments([]);
    }
    if (contactsForSend.length > 0) clearContactsForSend?.();
    // New-conversation handoffs clear in onConversationCreated (one transition,
    // once the thread id exists); existing threads clear now.
    if (!isNewConv) clearHandoff();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    turnInFlight,
    shouldAutosend,
    prefill,
    handoffOutreachId,
    handoffContext,
    handoffAttachments,
    isNewConv,
    selectedId,
    prefillFromStore,
    chat,
    router,
    pendingUploadFiles,
    uploadsInFlight,
    readyAttachments.length,
    pendingContacts,
    contactsForSend,
    lockedChannel,
    beginSendScroll,
    waitingForRecipient,
  ]);
}
